import {Route} from '@angular/router';
import {inject} from '@angular/core';
import {roleGuard} from "./auth/role-guard";
import {impersonationOrAutoLoginGuard} from "./auth/impersonation-or-auto-login.guard";
import {UserRole} from "./interfaces/api-interfaces";
import {KeycloakService} from "./services/keycloak.service";

export const routes: Route[] = [
    {
        path: '',
        redirectTo: '/login',
        pathMatch: 'full'
    },
    {
        path: 'login',
        loadComponent: () => import('./pages/login/login.component').then(m => m.Login)
    },
    {
        path: 'check-in',
        loadComponent: () => import('./pages/check-in/check-in.component').then(m => m.CheckInComponent),
        canActivate: [impersonationOrAutoLoginGuard],
    },
    {
        path: 'checkout',
        loadComponent: () => import('./pages/checkout/checkout.component').then(m => m.CheckoutComponent),
        canActivate: [impersonationOrAutoLoginGuard],
    },
    {
        path: 'checkout/success',
        loadComponent: () => import('./pages/checkout-success/checkout-success.component').then(m => m.CheckoutSuccessComponent),
        canActivate: [impersonationOrAutoLoginGuard],
    },
    {
        path: 'impersonate',
        loadComponent: () => import('./pages/impersonate/impersonate.component').then(m => m.ImpersonateComponent)
    },
    {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'home',
        loadComponent: () => import('./pages/learner-home/learner-home.component').then(m => m.LearnerHomeComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.TEILNEHMER, UserRole.DOZENT, UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'learning',
        loadComponent: () => import('./pages/learning/learning.component').then(m => m.LearningComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.TEILNEHMER]},
    },
    {
        path: 'materials',
        loadComponent: () => import('./pages/materials-library/materials-library.component').then(m => m.MaterialsLibraryComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.TEILNEHMER]},
    },
    {
        path: 'lesson/:courseId/:lessonId',
        loadComponent: () => import('./pages/lesson/lesson.component').then(m => m.LessonComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.TEILNEHMER]},
    },
    {
        path: 'lesson/:courseId/:lessonId/quiz/:quizId',
        loadComponent: () => import('./pages/quiz/quiz.component').then(m => m.QuizComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.TEILNEHMER]},
    },
    {
        path: 'courses',
        loadComponent: () => import('./pages/courses/courses.component').then(m => m.CoursesComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'course-admin',
        loadComponent: () => import('./pages/course-admin/course-admin.component').then(m => m.CourseAdminComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'users',
        loadComponent: () => import('./pages/users/users.component').then(m => m.UsersComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT, UserRole.DOZENT, UserRole.TEILNEHMER]},
    },
    {
        path: 'reports',
        loadComponent: () => import('./pages/reports/reports.component').then(m => m.ReportsComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'classes',
        loadComponent: () => import('./pages/classes/classes.component').then(m => m.ClassesComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'submissions',
        loadComponent: () => import('./pages/dozent-submissions/dozent-submissions.component').then(m => m.DozentSubmissionsComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'teaching',
        loadComponent: () => import('./pages/teaching/teaching.component').then(m => m.TeachingComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'teaching/:classId',
        loadComponent: () => import('./pages/teaching-class-detail/teaching-class-detail.component').then(m => m.TeachingClassDetailComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.DOZENT, UserRole.MASASANA, UserRole.FREYMUT]},
    },
    {
        path: 'community',
        loadComponent: () => import('./components/community/community-hub/community-hub.component').then(m => m.CommunityHubComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT, UserRole.DOZENT, UserRole.TEILNEHMER, UserRole.ABSOLVENT]},
    },
    {path: 'community/:id', redirectTo: '/community', pathMatch: 'full'},
    {path: 'buddy-groups', redirectTo: '/community', pathMatch: 'full'},
    {path: 'buddy-groups/:id', redirectTo: '/community', pathMatch: 'full'},
    {path: 'messages', redirectTo: '/community', pathMatch: 'full'},
    {path: 'messages/:id', redirectTo: '/community', pathMatch: 'full'},
    {
        path: 'settings',
        loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent),
        canActivate: [impersonationOrAutoLoginGuard, roleGuard],
        data: {roles: [UserRole.MASASANA, UserRole.FREYMUT, UserRole.DOZENT, UserRole.TEILNEHMER, UserRole.ABSOLVENT]},
        children: [
            {
                path: '',
                redirectTo: 'profile',
                pathMatch: 'full'
            },
            {
                path: 'profile',
                loadComponent: () => import('./pages/settings/profile/profile.component').then(m => m.ProfileSettingsComponent)
            },
            {
                path: 'security',
                loadComponent: () => import('./pages/settings/security/security.component').then(m => m.SecuritySettingsComponent)
            },
            {
                path: 'design',
                loadComponent: () => import('./pages/settings/design/design.component').then(m => m.DesignSettingsComponent)
            },
            {
                path: 'notifications',
                loadComponent: () => import('./pages/settings/notifications/notifications.component').then(m => m.NotificationsSettingsComponent)
            },
            {
                path: 'certificates',
                loadComponent: () => import('./pages/settings/certificates/certificates.component').then(m => m.CertificatesSettingsComponent)
            },
            {
                path: 'subscription',
                loadComponent: () => import('./pages/settings/subscription/subscription.component').then(m => m.SubscriptionSettingsComponent)
            }
        ]
    }
];
